import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #hasWrappedPaginationMethods = false;

  connectedCallback() {
    super.connectedCallback();

    this.#wrapPaginationMethods();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('paginated-list:change', this.#updateResultsFooter);
    this.setAttribute('initialized', '');
    requestIdleCallback(() => this.#updateResultsFooter());
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('paginated-list:change', this.#updateResultsFooter);
    super.disconnectedCallback();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #wrapPaginationMethods() {
    if (this.#hasWrappedPaginationMethods) return;

    const renderNextPage = this.renderNextPage.bind(this);
    const renderPreviousPage = this.renderPreviousPage.bind(this);

    this.renderNextPage = async (...args) => {
      try {
        return await renderNextPage(...args);
      } finally {
        this.#syncPaginatedState();
      }
    };

    this.renderPreviousPage = async (...args) => {
      try {
        return await renderPreviousPage(...args);
      } finally {
        this.#syncPaginatedState();
      }
    };

    this.#hasWrappedPaginationMethods = true;
  }

  #syncPaginatedState() {
    this.#updateResultsFooter();
    this.#updateSeoPagination();

    requestAnimationFrame(() => {
      this.#updateResultsFooter();
      this.#updateSeoPagination();
    });
  }

  #updateResultsFooter = () => {
    const footer = this.querySelector('[data-results-footer]');
    if (!(footer instanceof HTMLElement)) return;

    const seenTarget = footer.querySelector('[data-results-seen]');
    const totalTarget = footer.querySelector('[data-results-total]');
    const loadMoreButton = this.querySelector('[ref="loadMoreButton"]');
    const progressBar = this.querySelector('[ref="loadMoreProgress"]');
    const totalResults = Number(footer.dataset.totalResults || '0');
    const renderedResults = this.#getRenderedResultCount();
    const seenResults = totalResults > 0 ? Math.min(renderedResults, totalResults) : renderedResults;

    footer.dataset.seenResults = String(seenResults);

    if (seenTarget) seenTarget.textContent = String(seenResults);
    if (totalTarget) totalTarget.textContent = String(totalResults);

    if (progressBar instanceof HTMLElement && totalResults > 0) {
      progressBar.style.setProperty('--results-progress', `${(seenResults / totalResults) * 100}%`);
    }

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.hidden = totalResults > 0 && seenResults >= totalResults;
    }
  };

  #getRenderedResultCount() {
    const { grid } = this.refs;
    if (!grid) return 0;

    return grid.querySelectorAll(':scope > [data-product-id]').length;
  }

  #updateSeoPagination() {
    const footer = this.querySelector('[data-results-footer]');
    const paginationWrapper = footer?.querySelector('.exa-results-footer__seo-pagination');
    const lastCard = this.querySelector('[ref="grid"] > [data-page]:last-of-type');
    const lastRenderedPage = Number(lastCard?.dataset.page || '1');
    const pageHTML = this.pages.get(lastRenderedPage);

    if (!(paginationWrapper instanceof HTMLElement) || !pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const nextPaginationWrapper = parsedPage.querySelector('.exa-results-footer__seo-pagination');

    if (nextPaginationWrapper) {
      paginationWrapper.innerHTML = nextPaginationWrapper.innerHTML;
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
